import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getMethod,
  getMethodNoAuth,
  postMethodForSignin,
} from "../../services/ApiCallMethods";

// Get onboarded user data (no auth required)
export const getOnboarduser = createAsyncThunk(
  "setup/getOnboarduser",
  async (token, { rejectWithValue }) => {
    try {
      const response = await getMethodNoAuth(`/api/getUserData/${token}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to fetch user profile");
      }

      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch user profile");
    }
  }
);

// Thunk to check username availability
export const checkUsername = createAsyncThunk(
  "setup/checkUsername",
  async (username, { rejectWithValue }) => {
    try {
      const response = await getMethod(
        `/api/auth/check-username?username=${username}`
      );
      const data = await response.json();
      return {
        available: data?.available ?? false,
        message: data?.message ?? "",
      };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Username check failed."
      );
    }
  }
);

export const usernameSuggestion = createAsyncThunk(
  "setup/usernameSuggestion",
  async (input, { rejectWithValue }) => {
    try {
      const response = await getMethod(
        `/api/auth/username/suggestions?input=${input}`
      );
      const data = await response.json();
      return {
        suggestions: data?.suggestions ?? [],
        message: data?.message ?? "",
      };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Username suggestions failed."
      );
    }
  }
);

// Thunk to setup account
export const setupAccount = createAsyncThunk(
  "setup/setupAccount",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postMethodForSignin(
        "/api/auth/setup-account",
        payload
      );
      if (response.status === 409) {
        return {
          status: response.status,
          message: response.data?.message
        };
      }
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Account setup failed."
      );
    }
  }
);

const setupAccountSlice = createSlice({
  name: "setup",
  initialState: {
    loading: false,
    onboardloading: false,
    error: null,
    usernameAvailable: null,
    usernameMessage: "",
    accountSetupSuccess: false,
    usernameSuggestion: [],
    suggestionMessage: "",
    onboardUserData: null,
  },
  reducers: {
    resetSetupState: (state) => {
      state.loading = false;
      state.error = null;
      state.usernameAvailable = null;
      state.accountSetupSuccess = false;
      state.usernameSuggestion = [];
      state.suggestionMessage = "";
      state.usernameMessage = "";
      state.onboardUserData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOnboarduser.pending, (state) => {
        state.onboardloading = true;
        state.error = null;
      })
      .addCase(getOnboarduser.fulfilled, (state, action) => {
        state.onboardloading = false;
        state.onboardUserData = action.payload;
      })
      .addCase(getOnboarduser.rejected, (state, action) => {
        state.onboardloading = false;
        state.error = action.payload;
        state.onboardUserData = null;
      })
      .addCase(checkUsername.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.usernameAvailable = null;
        state.usernameMessage = "";
      })
      .addCase(checkUsername.fulfilled, (state, action) => {
        state.loading = false;
        state.usernameAvailable = action.payload?.available;
        state.usernameMessage = action.payload?.message;
        if (action.payload?.available === true) {
          state.usernameMessage = "";
        }
      })
      .addCase(checkUsername.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.usernameAvailable = null;
        state.usernameMessage = "";
      })

      // setupAccount
      .addCase(setupAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.accountSetupSuccess = false;
      })
      .addCase(setupAccount.fulfilled, (state) => {
        state.loading = false;
        state.accountSetupSuccess = true;
      })
      .addCase(setupAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.accountSetupSuccess = false;
      })
      // suggestion
      .addCase(usernameSuggestion.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.usernameSuggestion = null;
        state.suggestionMessage = "";
      })
      .addCase(usernameSuggestion.fulfilled, (state, action) => {
        state.loading = false;
        state.usernameSuggestion = action.payload.suggestions ?? [];
        state.suggestionMessage = action.payload.message ?? "";
      })
      .addCase(usernameSuggestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.usernameSuggestion = null;
        state.suggestionMessage = "";
      });
  },
});

export const { resetSetupState } = setupAccountSlice.actions;
export default setupAccountSlice.reducer;
